import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import RoutineDetailEntryPoint from '@databox/ember-integrations/lib/components/Ai/Routines/RoutineDetailEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { getShadowDomMode } from 'webapp/utils/common';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

interface GenieRoutinesDetailSignature {
	Element: HTMLDivElement;
	Args: {
		routineId: string;
	};
}

export default class GenieRoutinesDetailComponent extends Component<GenieRoutinesDetailSignature> {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('react') declare reactService: Service.React;

	reactElement: ReactIntegrations.Root = null;

	@action
	setupView(node: HTMLElement) {
		mountReactRoot(this, node, () => this.renderDetail());
	}

	@action
	teardownView() {
		unmountReactRoot(this);
	}

	@action
	handleRoutineIdChange() {
		this.renderDetail();
	}

	@action
	openRun(run: { id: string }) {
		// No thread guard: History renders a run without one as inert, so nothing arrives here by
		// mistake — and `Run now` hands over a queued run precisely to open it.
		this.aiChatService.onNavigateToRun(this.args.routineId, run.id);
	}

	private renderDetail() {
		if (!this.reactElement) {
			return;
		}

		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken: accessToken!,
			dataToken: dataToken!,
			spaceId: this.accountService.id,
			user: {
				id: Number(this.currentUserService.id),
				name: this.currentUserService.displayName,
			},
			trackingOrigin: this.aiChatService.trackingOrigin,
			showToast: this.aiChatService.showToast,
			routineId: this.args.routineId,
			onBack: this.aiChatService.onNavigateToRoutines,
			onSelectRun: this.openRun,
			onDeleted: this.aiChatService.onNavigateToRoutines,
			onUpgrade: this.aiChatService.onUpgrade,
			onAddCredits: this.aiChatService.onAddCredits,
			slack: this.aiChatService.slackHostProps,
		};

		this.reactService.render(
			this.reactElement,
			RoutineDetailEntryPoint(props),
			getShadowDomMode(this.accountService?.email),
		);
	}

	<template>
		<div
			{{didInsert this.setupView}}
			{{didUpdate this.handleRoutineIdChange @routineId}}
			{{willDestroy this.teardownView}}
			id="genie-routines-container"
			...attributes
		/>
	</template>
}
